import { Add, Delete, Edit } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import AlertDialog from "components/AlertDialog";
import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Dialog from "components/NoScrollDialog";
import { INTERNAL } from "const";
import { useLanguage } from "contexts/language.context";
import { NestedDropdown } from "mui-nested-menu";
import { useCallback, useEffect, useMemo, useState } from "react";
import areasService from "services/areas-service";
import auditsService from "services/audits-service";
import branchesService from "services/branches-service";
import useOpen from "utils/useOpen";
import AreaDialog from "./AreaDialog";
import ConsolidatedReportDialog from "./ConsolidatedReportDialog";
import { usePermissionMonitor } from "components/PermissionMonitor";

export default function ConsolidatedCreationReportDialog({
  open,
  setOpen,
  branchGroupId,
}) {
  const { label } = useLanguage();
  const [selected, setSelected] = useState([]);
  const [consolidatedReportDialogOpen, setConsolidatedReportDialogOpen] =
    useState(false);
  const [consolidatedReportType, setConsolidatedReportType] = useState(null);
  const [branchGroup, setBranchGroup] = useState(null);
  const [loading, setLoading] = useState({
    fetchAudits: 1,
    fetchReports: 0,
  });
  const [areas, setAreas] = useState(null);
  const [tab, setTab] = useState("audits");

  useEffect(() => {
    if (open && tab == "audits") {
      fetchAudits(false);
    }
    if (open) getAreas();
  }, [open, tab]);

  const handleClose = () => {
    setOpen(false);
  };

  const fetchAudits = async (load = true) => {
    if (load)
      setLoading((curr) => ({
        ...curr,
        fetchAudits: (curr.fetchAudits || 0) + 1,
      }));
    const [response, data] = await branchesService.getBranches({
      branchGroupId: branchGroupId,
      filterAuditsByGeneratedReports: true,
      includeColorPalette: true,
    });

    if (response.ok) {
      const _branchGroup = data[0];
      setBranchGroup(data[0]);
      setSelected(() => {
        return _branchGroup.branches.map((branch) => {
          return [];
        });
      });
    }

    setLoading((curr) => ({
      ...curr,
      fetchAudits:
        curr.fetchAudits <= 0 ? curr.fetchAudits : curr.fetchAudits - 1,
    }));
  };

  const fetchAuditReports = async () => {
    if (!selected?.flat(1)?.length) return;
    setLoading((curr) => ({
      ...curr,
      fetchReports: (curr.fetchReports || 0) + 1,
    }));
    const required = [];

    selected.flat(1).map((id) => {
      branchGroup.branches.forEach((branch) => {
        branch.audits.forEach((audit) => {
          if (audit.id == id && !audit.reportGenerated) {
            required.push(id);
          }
        });
      });
    });

    const [response, data] =
      await auditsService.getAssessmentReportsDataForConsolidated({
        auditIds: required,
      });
    if (response.ok) {
      setBranchGroup((branchGroup) => {
        const newBranchGroup = { ...branchGroup };

        newBranchGroup.branches.forEach((branch, branchIndex) => {
          branch.audits.forEach((audit, auditIndex) => {
            const newAudit = data.find((v) => v.id == audit.id);
            if (newAudit) {
              newBranchGroup.branches[branchIndex].audits[auditIndex] = {
                ...newAudit,
                type: audit.type,
              };
            }
          });
        });

        return newBranchGroup;
      });
    }

    setLoading((curr) => ({ ...curr, fetchReports: curr.fetchReports - 1 }));
  };

  async function getAreas() {
    setLoading((loading) => ({ ...loading, fetchAreas: true }));
    const [response, data] = await areasService.getAreas({ branchGroupId });
    if (response.ok) {
      setAreas(data);
    }

    setLoading((loading) => ({ ...loading, fetchAreas: false }));
  }

  const getBranches = useCallback(() => {
    return (
      branchGroup?.branches
        ?.map((branch, index) => {
          if (selected[index]?.length > 0) {
            const audits = branch.audits.filter((audit) =>
              selected[index].includes(audit.id)
            );
            return { ...branch, audits };
          } else {
            return { ...branch, audits: [] };
          }
        })
        ?.filter((branch) => branch.audits.length > 0) || []
    );
  }, [selected, branchGroup]);

  const regions = useMemo(() => {
    if (!branchGroup?.branches) return [];
    const allRegions = {};
    branchGroup.branches.map((branch) => {
      allRegions[branch.region.id] = branch.region;
    });
    return Object.values(allRegions);
  }, [branchGroup]);

  const branchesByRegion = useMemo(() => {
    const branches = {};
    branchGroup?.branches?.map((branch) => {
      if (!branches[branch.region.id]) branches[branch.region.id] = [];
      branches[branch.region.id].push(branch);
    });
    return branches;
  }, [branchGroup]);

  const allowComparative = useMemo(() => {
    let foundUnsupported = false;
    let total = 0;

    selected.forEach((items, index) => {
      let foundInternal = 0,
        foundExternal = 0;

      items.forEach((item) => {
        const audit = branchGroup.branches[index].audits.find(
          (audit) => audit.id == item
        );
        if (audit.type == INTERNAL) foundInternal++;
        else foundExternal++;
      });

      total += foundExternal + foundInternal;

      if (foundExternal == foundInternal && foundExternal == 0) return;

      if (foundExternal != 1 || foundInternal != 1) foundUnsupported = true;
    });

    if (!total) return false;
    return !foundUnsupported;
  }, [selected, branchGroup]);

  useEffect(() => {
    fetchAuditReports();
  }, [selected]);

  return (
    <>
      {consolidatedReportDialogOpen && (
        <ConsolidatedReportDialog
          isOpen={consolidatedReportDialogOpen && branchGroup}
          branches={getBranches()}
          selected={
            branchGroup
              ? branchGroup.branches.map((branch, index) => ({
                  id: branch.id,
                  selected: selected[index] || [],
                }))
              : []
          }
          branchGroup={branchGroup}
          type={consolidatedReportType}
          setIsOpen={setConsolidatedReportDialogOpen}
        />
      )}
      <Dialog
        fullWidth={true}
        maxWidth={"lg"}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            gap={2}
          >
            <MDTypography variant="h5">
              {label("generate-consolidated-report")}
            </MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <NestedDropdown
                ButtonProps={{
                  sx: {
                    backgroundColor: "info.main",
                    color: "white !important",

                    "&:hover": {
                      backgroundColor: "info.main",
                      color: "white !important",
                      opacity: 0.5,
                    },
                  },

                  disabled:
                    loading.fetchReports ||
                    loading.fetchAudits ||
                    selected?.filter((element) => element?.length > 0)
                      .length === 0,
                  loading: loading.fetchReports || loading.fetchAudits,
                }}
                MenuProps={{
                  sx: {
                    "& .MuiBox-root": {
                      "& .MuiTypography-root": {
                        fontSize: "14px",
                      },
                    },
                  },
                }}
                menuItemsData={{
                  label:
                    loading.fetchReports || loading.fetchAudits
                      ? label("loading")
                      : label("generate-button"),
                  items: [
                    {
                      label: label("generate-all-button"),
                      callback: () => {
                        setConsolidatedReportType({ value: "normal" });
                        setConsolidatedReportDialogOpen(Math.random());
                      },
                      sx: {
                        mx: -1,
                      },
                    },

                    {
                      label: label("generate-comparative-button"),
                      callback: () => {
                        setConsolidatedReportType({ value: "comparative" });
                        setConsolidatedReportDialogOpen(Math.random());
                      },
                      sx: {
                        mx: -1,
                      },
                      disabled: !allowComparative,
                    },

                    {
                      label: label("generate-by-region-button"),
                      items: regions?.map((region) => {
                        const active =
                          (selected.findIndex(
                            (s, index) =>
                              s.length === 0 &&
                              branchGroup.branches[index].region.id ===
                                region.id
                          ) !== -1 &&
                            selected.findIndex(
                              (s, index) =>
                                s.length !== 0 &&
                                branchGroup.branches[index].region.id ===
                                  region.id
                            ) !== -1) ||
                          selected.findIndex(
                            (s, index) =>
                              s.length === 0 &&
                              branchGroup.branches[index].region.id ===
                                region.id
                          ) === -1;

                        return {
                          label: region.description,
                          callback: () => {
                            setConsolidatedReportType({
                              value: "region",
                              region,
                            });
                            setConsolidatedReportDialogOpen(Math.random());
                          },
                          sx: {
                            "& .MuiBox-root": {
                              "& .MuiTypography-root": {
                                fontSize: "14px",
                              },
                            },
                            mx: -1,
                          },

                          disabled: !active,
                        };
                      }),
                      sx: {
                        mx: -1,
                      },
                    },
                    // {
                    //   label: label("generate-comprehensive"),
                    //   callback: () => {
                    //     setConsolidatedReportType({
                    //       value: "comprehensive",
                    //       regions,
                    //     });
                    //     setConsolidatedReportDialogOpen(Math.random());
                    //   },
                    //   disabled: !regions.reduce((prev, curr) => {
                    //     const active =
                    //       (selected.findIndex(
                    //         (s, index) =>
                    //           s.length === 0 &&
                    //           branchGroup.branches[index].region.id === curr.id
                    //       ) !== -1 &&
                    //         selected.findIndex(
                    //           (s, index) =>
                    //             s.length !== 0 &&
                    //             branchGroup.branches[index].region.id ===
                    //               curr.id
                    //         ) !== -1) ||
                    //       selected.findIndex(
                    //         (s, index) =>
                    //           s.length === 0 &&
                    //           branchGroup.branches[index].region.id === curr.id
                    //       ) === -1;

                    //     return prev && active;
                    //   }, true),
                    //   sx: {
                    //     mx: -1,
                    //   },
                    // },
                    {
                      label: label("generate-comprehensive"),
                      callback: () => {
                        setConsolidatedReportType({
                          value: "comprehensive",
                          areas,
                        });
                        setConsolidatedReportDialogOpen(Math.random());
                      },
                      disabled: !areas?.find((area) =>
                        getBranches()?.find(
                          (branch) => branch.areaId == area.id
                        )
                      ),
                      sx: {
                        mx: -1,
                      },
                    },
                  ],
                }}
              />

              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2, pb: 2 }}>
            <MDButton
              variant={tab == "audits" ? "gradient" : "outlined"}
              color="info"
              sx={{ flex: 1 }}
              onClick={() => setTab("audits")}
            >
              {label("audit-plural")}
            </MDButton>
            <MDButton
              sx={{ flex: 1 }}
              variant={tab == "areas" ? "gradient" : "outlined"}
              color="info"
              onClick={() => setTab("areas")}
            >
              {label("area-plural")}
            </MDButton>
          </Box>
          {tab == "audits" ? (
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <Grid container>
                <Grid container item xs={12}>
                  <Grid item xs={12}>
                    <Collapse
                      style={{ width: "100%" }}
                      in={open && !loading.fetchAudits}
                    >
                      {/* <FormControlLabel
                      label={`${label("select-all")} (${
                        selected?.filter((element) => element?.length > 0)
                          .length
                      }/${selected.length})`}
                      control={
                        <Checkbox
                          checked={
                            selected.findIndex(
                              (s, index) =>
                                s.length !==
                                branchGroup?.branches[index]?.audits?.length
                            ) === -1
                          }
                          indeterminate={
                            selected.findIndex(
                              (s, index) =>
                                s.length ===
                                branchGroup?.branches[index]?.audits?.length
                            ) !== -1 &&
                            selected.findIndex(
                              (s, index) =>
                                s.length !==
                                branchGroup?.branches[index]?.audits?.length
                            ) !== -1
                          }
                          indeterminateIcon={<IndeterminateIcon />}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              setSelected((curr) => {
                                const newSelected = [...curr];
                                branchGroup?.branches?.map((branch, index) => {
                                  newSelected[index] = branch?.audits?.map(
                                    (audit) => audit.id
                                  );
                                });

                                return newSelected;
                              });
                            } else {
                              setSelected((curr) => {
                                const newSelected = [...curr];
                                branchGroup?.branches?.map((_, index) => {
                                  newSelected[index] = [];
                                });
                                return newSelected;
                              });
                            }
                          }}
                        />
                      }
                    />
                    {regions.map((region) => {
                      return (
                        <FormControlLabel
                          key={"Region: " + region.id}
                          label={`${label("select-all-from")} ${region.description.trim()} (${
                            selected.filter((value, index) => {
                              return (
                                value.length > 0 &&
                                branchGroup.branches[index].region.id ==
                                  region.id
                              );
                            }).length
                          }/${branchesByRegion[region.id].length})`}
                          control={
                            <Checkbox
                              checked={
                                selected.findIndex(
                                  (s, index) =>
                                    s.length === 0 &&
                                    branchGroup.branches[index].region.id ===
                                      region.id
                                ) === -1
                              }
                              indeterminate={
                                selected.findIndex(
                                  (s, index) =>
                                    s.length === 0 &&
                                    branchGroup.branches[index].region.id ===
                                      region.id
                                ) !== -1 &&
                                selected.findIndex(
                                  (s, index) =>
                                    s.length !== 0 &&
                                    branchGroup.branches[index].region.id ===
                                      region.id
                                ) !== -1
                              }
                              indeterminateIcon={<IndeterminateIcon />}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  setSelected((curr) => {
                                    const newSelected = [...curr];
                                    branchGroup?.branches?.map(
                                      (branch, index) => {
                                        if (branch.region.id === region.id)
                                          newSelected[index] =
                                            branch?.audits?.map(
                                              (audit) => audit.id
                                            );
                                        else {
                                        }
                                      }
                                    );

                                    return newSelected;
                                  });
                                } else {
                                  setSelected((curr) => {
                                    const newSelected = [...curr];
                                    branchGroup?.branches?.map(
                                      (branch, index) => {
                                        if (branch.region.id === region.id)
                                          newSelected[index] = [];
                                      }
                                    );
                                    return newSelected;
                                  });
                                }
                              }}
                            />
                          }
                        />
                      );
                    })}
                    <Divider /> */}
                      {branchGroup?.branches.map((branch, index) => {
                        return (
                          <ListItem
                            branch={branch}
                            selected={selected}
                            branchIndex={index}
                            setSelected={setSelected}
                            loading={loading}
                          />
                        );
                      })}
                    </Collapse>
                  </Grid>

                  {loading.fetchAudits && (
                    <Grid
                      item
                      xs={12}
                      style={{
                        width: "100%",
                        height: "200px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress
                        sx={{ margin: "0 auto" }}
                        color="info"
                        size={30}
                        thickness={6}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          ) : null}
          {tab == "areas" ? (
            <AreasTab
              areas={areas}
              getAreas={getAreas}
              branchGroupId={branchGroupId}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AreasTab({ areas, getAreas, branchGroupId }) {
  const [loading, setLoading] = useState({ fetch: false });
  const { label } = useLanguage();
  const open = useOpen();
  const [branches, setBranches] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [currentArea, setCurrentArea] = useState(null);
  const can = usePermissionMonitor();

  async function getBranches() {
    setLoading((loading) => ({ ...loading, fetchBranches: true }));
    const [response, data] = await branchesService.getBranches({
      branchGroupId,
    });
    if (response.ok) {
      setBranches(data[0].branches);
    }
    setLoading((loading) => ({ ...loading, fetchBranches: false }));
  }

  async function createArea(area) {
    setLoading((loading) => ({ ...loading, create: true }));
    const [response, data] = await areasService.createArea(area);

    if (response.ok) {
      await getAreas();
      setLoading((loading) => ({ ...loading, create: false }));
      return { success: true };
    } else {
      setLoading((loading) => ({ ...loading, create: false }));
      return { error: data.error };
    }
  }

  async function updateBranchArea(branchId, areaId) {
    setLoading((loading) => ({ ...loading, update: true }));
    if (!areaId) {
      const oldArea = areas.find((area) =>
        area.branches.find((branch) => branch.id == branchId)
      );

      if (!oldArea) return;

      const [response, data] = await areasService.updateArea(oldArea.id, {
        branches: oldArea.branches
          .filter((branch) => branch.id != branchId)
          .map((v) => ({ id: v.id })),
      });
      if (response.ok) {
        Promise.all([getAreas(), getBranches()]);
      }
    } else {
      const [response, data] = await areasService.updateArea(areaId, {
        branches: [...branchesByArea[areaId], { id: branchId }],
      });
      if (response.ok) {
        Promise.all([getAreas(), getBranches()]);
      }
    }
    setLoading((loading) => ({ ...loading, update: false }));
  }

  async function updateArea(areaId, area) {
    setLoading((loading) => ({ ...loading, update: true }));
    const [response, data] = await areasService.updateArea(areaId, area);
    if (response.ok) {
      await getAreas();
      setLoading((loading) => ({ ...loading, update: false }));
      return { success: true };
    } else {
      setLoading((loading) => ({ ...loading, update: false }));
      return { error: data.error };
    }
  }

  async function deleteArea(areaId) {
    const [response, data] = await areasService.removeArea(areaId);
    if (response.ok) {
      Promise.all([getAreas(), getBranches()]);
      setLoading((loading) => ({ ...loading, update: false }));
      return { success: true };
    } else {
      setLoading((loading) => ({ ...loading, update: false }));
      return { error: data.error };
    }
  }

  const onDrag = (branch) => (e) => {
    setCurrentBranch(branch);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = () => (e) => {
    e.preventDefault();
    updateBranchArea(currentBranch.id, currentArea?.id);
  };

  const onDragOver = (area) => (e) => {
    setCurrentArea(area);
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const branchesByArea = useMemo(() => {
    if (!areas || !branches) return {};

    const result = {};
    areas?.map((area) => {
      result[area.id] = branches.filter((branch) => branch?.areaId == area.id);
    });

    result["none"] = branches.filter(
      (branch) => !areas?.map((s) => s.id).includes(branch?.areaId)
    );
    return result;
  }, [branches, areas]);

  useEffect(() => {
    Promise.all([getAreas(), getBranches()]);
  }, []);

  return (
    <>
      {open.get("dialog") ? (
        <AreaDialog
          isOpen={open.get("dialog")}
          onSubmit={(area) => {
            if (open.get("dialog").type == "create") {
              return createArea(area);
            } else {
              return updateArea(area.id, area);
            }
          }}
          data={open.get("dialog")?.data}
          type={open.get("dialog")?.type}
          branchGroupId={branchGroupId}
        />
      ) : null}

      {open.get("alert") && (
        <AlertDialog
          open={open.get("alert")}
          setOpen={(value) => {
            open.change("alert", value);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await deleteArea(open.get("alert")?.data);
          }}
        />
      )}

      {loading.fetchAreas || loading.fetchBranches ? (
        <div
          style={{
            height: "500px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="info" />
        </div>
      ) : (
        <Grid container spacing={2}>
          {can("create", "area") ? (
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => {
                  open.change("dialog", { type: "create" });
                }}
              >
                <Add />
                &nbsp; {label("create-new-area-button")}
              </MDButton>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Card
              sx={{
                px: 1.5,
                py: 1,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              }}
              variant="outlined"
              onDrop={onDrop()}
              onDragOver={onDragOver(null)}
            >
              <MDTypography variant="h6" sx={{ marginBottom: 1 }}>
                {label("branches-with-no-area")}
              </MDTypography>
              <Grid container spacing={1}>
                {branchesByArea["none"]?.length > 0 ? (
                  branchesByArea["none"]?.map((branch) => {
                    return (
                      <Grid key={branch.id} item>
                        <MDBadge
                          size="lg"
                          badgeContent={
                            <>
                              <Icon
                                fontSize="medium"
                                sx={{
                                  margin: "-10px",
                                  marginLeft: "-16px",
                                  marginRight: "0px",
                                }}
                              >
                                drag_indicator
                              </Icon>
                              {branch.name}
                            </>
                          }
                          variant="gradient"
                          color="primary"
                          container
                          sx={{ cursor: "move" }}
                          draggable={Boolean(can("update", "area"))}
                          onDrag={
                            can("update", "area") ? onDrag(branch) : () => {}
                          }
                        />
                      </Grid>
                    );
                  })
                ) : (
                  <MDTypography
                    variant="caption"
                    sx={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "rgba(0, 0, 0, 0.5)",
                      padding: "0px 8px",
                      paddingTop: "8px",
                    }}
                  >
                    {label("no-branches-with-no-area")}
                  </MDTypography>
                )}
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ mb: -1 }}>
            <MDTypography
              variant="h6"
              sx={{ marginTop: 2, marginBottom: 0, pb: 0 }}
            >
              {label("area-plural")}
            </MDTypography>
          </Grid>
          {!areas?.length ? (
            <MDTypography
              variant="caption"
              sx={{
                fontSize: "14px",
                fontWeight: "400",
                color: "rgba(0, 0, 0, 0.5)",
                padding: "0px 16px",
                paddingTop: "8px",
              }}
            >
              {label("no-areas-were-found")}
            </MDTypography>
          ) : (
            ""
          )}
          {areas?.map((area) => (
            <Grid alignItems="stretch" item xs={6} key={area.id}>
              <Card
                sx={{
                  px: 1.5,
                  py: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                }}
                variant="outlined"
                onDrop={onDrop()}
                onDragOver={onDragOver(area)}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <MDTypography variant="h6" sx={{ marginBottom: 1 }}>
                    {area.name}
                  </MDTypography>
                  <Box>
                    {can("update", "area") ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          open.change("dialog", { type: "edit", data: area });
                        }}
                      >
                        <Edit />
                      </IconButton>
                    ) : null}
                    {can("delete", "area") ? (
                      <IconButton size="small">
                        <Delete
                          onClick={() => {
                            open.change("alert", { data: area.id });
                          }}
                        />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>

                <Grid container spacing={1}>
                  {branchesByArea[area.id]?.length > 0 ? (
                    branchesByArea[area.id]?.map((branch) => {
                      return (
                        <Grid key={branch.id} item>
                          <MDBadge
                            size="lg"
                            badgeContent={
                              <>
                                <Icon
                                  fontSize="medium"
                                  sx={{
                                    margin: "-10px",
                                    marginLeft: "-16px",
                                    marginRight: "0px",
                                  }}
                                >
                                  drag_indicator
                                </Icon>
                                {branch.name}
                              </>
                            }
                            variant="gradient"
                            color="primary"
                            container
                            sx={{ cursor: "move" }}
                            draggable={Boolean(can("update", "area"))}
                            onDrag={
                              can("update", "area") ? onDrag(branch) : () => {}
                            }
                          />
                        </Grid>
                      );
                    })
                  ) : (
                    <MDTypography
                      variant="caption"
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "rgba(0, 0, 0, 0.5)",
                        padding: "0px 8px",
                        paddingTop: "8px",
                      }}
                    >
                      {label("no-branches-in-area")}
                    </MDTypography>
                  )}
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}

function ListItem({ branch, selected, branchIndex, setSelected, loading }) {
  const { label } = useLanguage();
  return (
    <MDBox sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          width: "100%",
          fontWeight: "500",
          fontSize: "16px",
          py: 1,
        }}
      >
        <span>{branch?.name}</span>
        <Box>
          <MDBadge badgeContent={branch?.region.description} />
          {branch?.area ? (
            <MDBadge badgeContent={branch?.area?.name} color="primary" />
          ) : null}
        </Box>
      </Box>
      {/* <FormControlLabel
        label={
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{branch?.name}</span>
            <MDBadge badgeContent={branch?.region.description} />
          </div>
        }
        sx={{
          width: "100%",
          ".MuiFormControlLabel-label": {
            width: "calc(100% - 45px)",
          },
        }}
        control={
          <Checkbox
            checked={selected[branchIndex]?.length == branch?.audits?.length}
            indeterminate={
              selected[branchIndex]?.length !== 0 &&
              selected[branchIndex]?.length !== branch?.audits?.length
            }
            indeterminateIcon={<IndeterminateIcon />}
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                setSelected((curr) => {
                  const newSelected = [...curr];
                  newSelected[branchIndex] = branch?.audits?.map(
                    (audit) => audit.id
                  );
                  return newSelected;
                });
              } else {
                setSelected((curr) => {
                  const newSelected = [...curr];
                  newSelected[branchIndex] = [];
                  return newSelected;
                });
              }
            }}
          />
        }
      /> */}
      <MDBox sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            m: 0,
            px: 1.5,
            overflow: "hidden",
          }}
          variant="outlined"
        >
          <MDBox
            sx={(theme) => ({
              borderBottom: "1px solid",
              borderColor: theme.borders.borderColor,
              backgroundColor: theme.palette.info.main + "22",
              mx: -1.5,
              px: 1.5,
              py: 1,
            })}
          >
            <MDTypography variant="h6">{label("internal")}</MDTypography>
          </MDBox>
          <CardContent sx={{ p: 1, m: 0 }}>
            {branch?.audits
              ?.filter((audit) => audit.type == INTERNAL)
              ?.map((audit) => {
                return (
                  <FormControlLabel
                    key={"Audit: " + audit.id}
                    sx={{
                      marginTop: 0,
                      marginBottom: 0,
                    }}
                    label={audit?.description}
                    control={
                      <Checkbox
                        disabled={Boolean(loading?.fetchReports)}
                        checked={selected[branchIndex]?.includes(audit.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelected((curr) => {
                              const newSelected = [...curr];
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id
                              );

                              if (index == -1) {
                                newSelected[branchIndex].push(audit.id);
                              }

                              return newSelected;
                            });
                          } else {
                            setSelected((curr) => {
                              const newSelected = [...curr];
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id
                              );

                              newSelected[branchIndex].splice(index, 1);
                              return newSelected;
                            });
                          }
                        }}
                      />
                    }
                  />
                );
              })}
          </CardContent>
        </Card>
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            m: 0,
            px: 1.5,
            overflow: "hidden",
          }}
          variant="outlined"
        >
          <MDBox
            sx={(theme) => ({
              borderBottom: "1px solid",
              borderColor: theme.borders.borderColor,
              backgroundColor: theme.palette.primary.main + "22",
              mx: -1.5,
              px: 1.5,
              py: 1,
            })}
          >
            <MDTypography variant="h6">{label("external")}</MDTypography>
          </MDBox>
          <CardContent sx={{ p: 1, m: 0 }}>
            {branch?.audits
              ?.filter((audit) => audit.type != INTERNAL)
              .map((audit) => {
                return (
                  <FormControlLabel
                    key={"Audit: " + audit.id}
                    sx={{
                      marginTop: 0,
                      marginBottom: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 42px)",
                      },
                    }}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          {audit?.description}
                        </Typography>
                        {audit.type == 3 ? (
                          <MDBadge
                            color="primary"
                            badgeContent={label("secondary-auditor")}
                          />
                        ) : null}
                      </Box>
                    }
                    fullWidth
                    control={
                      <Checkbox
                        disabled={Boolean(loading?.fetchReports)}
                        checked={selected[branchIndex]?.includes(audit.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelected((curr) => {
                              const newSelected = [...curr];
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id
                              );

                              if (index == -1) {
                                newSelected[branchIndex].push(audit.id);
                              }

                              return newSelected;
                            });
                          } else {
                            setSelected((curr) => {
                              const newSelected = [...curr];
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id
                              );

                              newSelected[branchIndex].splice(index, 1);
                              return newSelected;
                            });
                          }
                        }}
                      />
                    }
                  />
                );
              })}
          </CardContent>
        </Card>
      </MDBox>
      <Divider />
    </MDBox>
  );
}
